/**
 * Canonical assessment completeness & evidence model.
 *
 * ONE source of truth for:
 *   - per-question answered / evidence state
 *   - per-category (domain) completeness used by the left navigation
 *   - the "can this be submitted?" decision surfaced by the Submit control
 *   - the block list produced when Next / Submit is pressed
 *
 * The rules implemented here mirror `public.respondent_submit_assessment`
 * exactly, so the client never claims a submission is valid when the server
 * would reject it (and never blocks a submission the server would accept).
 *
 * Important semantics:
 *   - A score of 0 ("Nonexistent") is a VALID ANSWER. Answered-ness is always
 *     `score != null`, never a truthiness check.
 *   - Evidence means a persisted file or link on the question. A comment only
 *     satisfies the evidence rule when the methodology explicitly permits it
 *     (telecom-style questionnaires), matching the server rule.
 */

import {
  isSelfAssessmentOnly,
  isTelecomStyleQuestionnaire,
  requiresAllQuestionsAnswered,
} from "@/lib/questionnaireType";

export type CompletenessResponse = {
  score: number | null;
  comment: string | null;
  evidence_links?: string[];
};

export type CompletenessRow = {
  question_id: number;
  domain: string;
  title?: string | null;
  requires_evidence?: boolean;
  evidence_threshold?: number | null;
};

export type EvidencePolicy = {
  /** Every question must be answered before submission. */
  requireAllAnswered: boolean;
  /** How evidence is enforced. */
  evidenceMode: "none" | "threshold_any" | "flagged";
  /** Methodology explicitly accepts a comment in place of evidence. */
  commentSatisfiesEvidence: boolean;
  /** Default threshold at/above which evidence is expected. */
  defaultThreshold: number;
};

/** Resolve the submission policy for a questionnaire (mirrors the server). */
export function resolveEvidencePolicy(questionnaireId: string | null | undefined): EvidencePolicy {
  const self = isSelfAssessmentOnly(questionnaireId);
  const telecomStyle = isTelecomStyleQuestionnaire(questionnaireId);

  if (self) {
    // Self-assessments: all questions required, no evidence validation at all.
    return {
      requireAllAnswered: requiresAllQuestionsAnswered(questionnaireId),
      evidenceMode: "none",
      commentSatisfiesEvidence: false,
      defaultThreshold: 50,
    };
  }

  if (telecomStyle) {
    // Telecom methodology explicitly permits evidence, link OR comment.
    return {
      requireAllAnswered: requiresAllQuestionsAnswered(questionnaireId),
      evidenceMode: "threshold_any",
      commentSatisfiesEvidence: true,
      defaultThreshold: 50,
    };
  }

  return {
    requireAllAnswered: requiresAllQuestionsAnswered(questionnaireId),
    evidenceMode: "flagged",
    commentSatisfiesEvidence: false,
    defaultThreshold: 50,
  };
}

/** A score of 0 is a valid answer — never use truthiness here. */
export function isAnswered(resp: CompletenessResponse | undefined | null): boolean {
  return resp?.score != null;
}

export function hasAttachedEvidence(resp: CompletenessResponse | undefined | null): boolean {
  return (resp?.evidence_links?.length ?? 0) > 0;
}

export function hasComment(resp: CompletenessResponse | undefined | null): boolean {
  return !!(resp?.comment && resp.comment.trim().length > 0);
}

export type QuestionIssue = "unanswered" | "evidence_missing";

export type QuestionState = {
  question_id: number;
  domain: string;
  answered: boolean;
  issue: QuestionIssue | null;
};

export type DomainState = {
  domain: string;
  total: number;
  answered: number;
  unanswered: number[];
  missingEvidence: number[];
  complete: boolean;
};

export type CompletenessResult = {
  policy: EvidencePolicy;
  questions: QuestionState[];
  byDomain: Record<string, DomainState>;
  unanswered: number[];
  missingEvidence: number[];
  totalQuestions: number;
  answeredQuestions: number;
  progressPercent: number;
  /** True when nothing blocks submission (excluding questionnaire extras like HRV vendors). */
  canSubmit: boolean;
  /** Human-readable reason the Submit control is disabled, or null. */
  blockingReason: string | null;
};

/** Does this question satisfy the evidence rule for the given policy? */
export function questionSatisfiesEvidence(
  row: CompletenessRow,
  resp: CompletenessResponse | undefined,
  policy: EvidencePolicy,
): boolean {
  if (policy.evidenceMode === "none") return true;
  if (!resp || resp.score == null) return true; // unanswered is reported separately

  const threshold =
    policy.evidenceMode === "flagged"
      ? row.evidence_threshold ?? policy.defaultThreshold
      : policy.defaultThreshold;

  if (policy.evidenceMode === "flagged" && !row.requires_evidence) return true;
  if (resp.score < threshold) return true;

  if (hasAttachedEvidence(resp)) return true;
  if (policy.commentSatisfiesEvidence && hasComment(resp)) return true;
  return false;
}

export function evaluateAssessmentCompleteness(args: {
  questionnaireId: string | null | undefined;
  rows: CompletenessRow[];
  responsesById: Map<number, CompletenessResponse>;
  domainOrder?: string[];
}): CompletenessResult {
  const { questionnaireId, rows, responsesById } = args;
  const policy = resolveEvidencePolicy(questionnaireId);

  const domains = args.domainOrder?.length
    ? args.domainOrder
    : Array.from(new Set(rows.map((r) => r.domain)));

  const byDomain: Record<string, DomainState> = {};
  for (const d of domains) {
    byDomain[d] = { domain: d, total: 0, answered: 0, unanswered: [], missingEvidence: [], complete: true };
  }

  const questions: QuestionState[] = [];
  const unanswered: number[] = [];
  const missingEvidence: number[] = [];

  for (const row of rows) {
    const resp = responsesById.get(row.question_id);
    const answered = isAnswered(resp);
    let issue: QuestionIssue | null = null;

    if (!answered) {
      issue = "unanswered";
      unanswered.push(row.question_id);
    } else if (!questionSatisfiesEvidence(row, resp, policy)) {
      issue = "evidence_missing";
      missingEvidence.push(row.question_id);
    }

    questions.push({ question_id: row.question_id, domain: row.domain, answered, issue });

    const bucket = byDomain[row.domain];
    if (bucket) {
      bucket.total += 1;
      if (answered) bucket.answered += 1;
      if (issue === "unanswered") bucket.unanswered.push(row.question_id);
      if (issue === "evidence_missing") bucket.missingEvidence.push(row.question_id);
    }
  }

  for (const d of Object.keys(byDomain)) {
    const b = byDomain[d];
    const answeredOk = policy.requireAllAnswered ? b.unanswered.length === 0 : true;
    b.complete = answeredOk && b.missingEvidence.length === 0;
  }

  const totalQuestions = rows.length;
  const answeredQuestions = totalQuestions - unanswered.length;
  const progressPercent =
    totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

  let blockingReason: string | null = null;
  if (policy.requireAllAnswered && unanswered.length > 0) {
    blockingReason = `${unanswered.length} question${unanswered.length > 1 ? "s" : ""} still unanswered.`;
  } else if (missingEvidence.length > 0) {
    blockingReason = policy.commentSatisfiesEvidence
      ? `${missingEvidence.length} question${missingEvidence.length > 1 ? "s" : ""} need evidence, a link, or a comment.`
      : `${missingEvidence.length} question${missingEvidence.length > 1 ? "s" : ""} need supporting evidence.`;
  }

  return {
    policy,
    questions,
    byDomain,
    unanswered,
    missingEvidence,
    totalQuestions,
    answeredQuestions,
    progressPercent,
    canSubmit: blockingReason === null,
    blockingReason,
  };
}

/** Blocking issues for a single category, used by the Next button. */
export function categoryBlockingIds(result: CompletenessResult, domain: string): number[] {
  const b = result.byDomain[domain];
  if (!b) return [];
  return [...b.unanswered, ...b.missingEvidence];
}
